import { useContext, useMemo } from 'react'

import { getPropertyMetasearchPriceAccuracyEventData } from 'analytics/metasearchPriceAccuracyTracking'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useFireMetasearchPriceAccuracyEvent from 'hooks/OfferPage/useFireMetasearchPriceAccuracyEvent'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import { useAppSelector } from 'hooks/reduxHooks'
import {
  getPropertyCheapestHotelOnlyRateForDiscount,
  getPropertyMetasearchPriceAccuracyRate,
} from 'selectors/offerPage/offerPageSelectors'

/**
 * Fires the SPP `metasearch_price_accuracy` event. All rate/price derivation stays in
 * `offerPageSelectors` (per the `PropertyRoomOptions` "no derivation here" guard); this hook
 * only reads those selectors and delegates the once-only firing to the shared fire hook.
 */
export default function usePropertyMetasearchPriceAccuracyTracking() {
  const offerPageState = useContext(OfferPageStateContext)
  const { checkInDate: checkIn, checkOutDate: checkOut, rooms } = offerPageState

  const ghaPrice = useAppSelector(appState => appState.router.location.query.gha_price)
  const metasearchRoomRateId = useAppSelector(appState => (
    appState.router.location.query.roomRateId || appState.router.location.query.roomOptionRateId
  ))
  const rate = useAppSelector(appState => getPropertyMetasearchPriceAccuracyRate(offerPageState, appState))
  const cheapestHotelOnlyDiscountRate = useAppSelector(appState => (
    getPropertyCheapestHotelOnlyRateForDiscount(offerPageState, appState)
  ))

  // Only apply the discount when the tracked first-tile rate IS the discount-eligible rate.
  const isMetasearchDiscountEligibleRate = (
    rate?.service.type === 'bedbank' &&
    cheapestHotelOnlyDiscountRate?.service.type === 'bedbank' &&
    cheapestHotelOnlyDiscountRate.service.propertyId === rate.service.propertyId &&
    cheapestHotelOnlyDiscountRate.rateId === rate.rateId
  )
  const [discountAmount, discountFetching, discountSettled] = useMetasearchDiscount(rate?.totals.displayPrice ?? 0, {
    propertyId: isMetasearchDiscountEligibleRate ? rate.service.propertyId : undefined,
    checkIn,
    checkOut,
    rooms,
    disabled: !isMetasearchDiscountEligibleRate,
  })

  // For a bedbank rate, `rate.service.propertyId` is the bedbank offer id — the metasearch
  // `propertyId=bedbank:…` id we join on downstream. A non-bedbank rate yields `undefined` and
  // short-circuits the event; this flow only ever monitors bedbank.
  const bedbankOfferId = rate?.service.type === 'bedbank' ? rate.service.propertyId : undefined
  const eventData = useMemo(() => (
    bedbankOfferId ?
        getPropertyMetasearchPriceAccuracyEventData({
          offerId: bedbankOfferId,
          pageType: 'spp',
          metasearchRoomRateId,
          rate,
          discountAmount,
        }) :
      undefined
  ), [bedbankOfferId, discountAmount, metasearchRoomRateId, rate])

  useFireMetasearchPriceAccuracyEvent(
    eventData,
    ghaPrice === '1' && !discountFetching && discountSettled,
    `spp:${bedbankOfferId ?? ''}`,
  )
}
